import {
  isISO8601DateTime,
  isISO8601DateOnly,
  isDateSame,
  isDateBefore as isDateBeforeUtil,
  isDateAfter as isDateAfterUtil,
} from '@ardoq/date-time';
import { Locale } from '@ardoq/locale';
import { isEqual } from 'lodash';

export const requiredString = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }
  if (value.trim().length === 0) {
    return false;
  }
  return true;
};

export const requiredArray = (value: unknown): value is unknown[] => {
  return Array.isArray(value);
};

export const equals = (valueA: unknown, valueB: unknown) => {
  return isEqual(valueA, valueB);
};

export const notEquals = (valueA: unknown, valueB: unknown) => {
  return !isEqual(valueA, valueB);
};

export const minLength = (value: unknown, length: number) => {
  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length >= length;
  }
  return false;
};

export const maxLength = (value: unknown, length: number) => {
  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length <= length;
  }
  return false;
};

export const includedIn = (
  searchElement: unknown,
  array: unknown[]
): boolean => {
  return array.some(arrayItem => isEqual(arrayItem, searchElement));
};

export const notIncludedIn = (
  searchElement: unknown,
  array: unknown[]
): boolean => {
  return !array.some(arrayItem => isEqual(arrayItem, searchElement));
};

export const subsetOf = (elements: unknown, array: unknown[]): boolean => {
  if (!Array.isArray(elements)) {
    return false;
  }
  return elements.every(searchElement => includedIn(searchElement, array));
};

export const isEmail = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }
  if (/.+@.+/.test(value)) {
    return true;
  }
  return false;
};

export const isBoolean = (value: unknown): value is boolean => {
  return typeof value === 'boolean';
};

const isFinite = (value: unknown): value is number => {
  return Number.isFinite(value);
};

export const isNumber = (value: unknown): value is number => {
  return isFinite(value);
};

export const isSafeInteger = (value: unknown): value is number => {
  if (!isFinite(value)) {
    return false;
  }
  if (value > Number.MAX_SAFE_INTEGER) {
    return false;
  }
  if (value < Number.MIN_SAFE_INTEGER) {
    return false;
  }
  return true;
};

export const isNumberGreaterThan = (
  value: unknown,
  minimum: number
): value is number => {
  if (!isFinite(value)) {
    // Positive infinity is greater than any number
    if (value === Number.POSITIVE_INFINITY) {
      return true;
    }
    return false;
  }

  if (value < minimum) {
    return false;
  }

  return true;
};

export const isNumberLowerThan = (
  value: unknown,
  maximum: number
): value is number => {
  if (!isFinite(value)) {
    // Negative infinity is lower than any number
    if (value === Number.NEGATIVE_INFINITY) {
      return true;
    }

    return false;
  }

  if (value > maximum) {
    return false;
  }

  return true;
};

export const isDateBefore = (
  fieldValue: unknown,
  otherFieldValue: string,
  locale: Locale
) => {
  if (typeof fieldValue !== 'string') return false;

  return (
    isDateSame(fieldValue, otherFieldValue, locale) ||
    isDateBeforeUtil(fieldValue, otherFieldValue, locale)
  );
};

export const isDateAfter = (
  fieldValue: unknown,
  otherFieldValue: string,
  locale: Locale
) => {
  if (typeof fieldValue !== 'string') return false;

  return (
    isDateSame(fieldValue, otherFieldValue, locale) ||
    isDateAfterUtil(fieldValue, otherFieldValue, locale)
  );
};

export const isDateOnlyISO8601 = (dateString: unknown) => {
  if (typeof dateString !== 'string') {
    return false;
  }
  return isISO8601DateOnly(dateString);
};

export const isDateTimeISO8601 = (dateString: unknown) => {
  if (typeof dateString !== 'string') {
    return false;
  }
  return isISO8601DateTime(dateString);
};

export const passwordComplexity = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }
  return /^(?=.*[A-Z0-9]).*$/.test(value);
};

export const isValidArdoqId = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  return /^[a-f0-9]{24}$/.test(value);
};

const TAG_NAME_REGEX = /^[a-z\d_\-æøåäöëèé]+$/i;

export const TAG_NAME_VALIDATION_MESSAGE = `Can only include regular alphabet characters, numbers, “_” and “-”`;

export const isValidTagName = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  return TAG_NAME_REGEX.test(value);
};

export const isEveryValidTagName = (value: unknown): value is string[] => {
  if (!Array.isArray(value)) return false;
  return value.every(isValidTagName);
};

/**
 * This one is used by grid editor - does not let you type an invalid character.
 * It is ment to be used per character, not per entire tag.
 */
export const isValidTagCharacter = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  return TAG_NAME_REGEX.test(value);
};

export const isValidUrl = (urlString: unknown): boolean => {
  try {
    const url = new URL(urlString as string);
    const hostname = url.hostname;
    return (
      hostname.includes('.') && hostname.indexOf('.') !== hostname.length - 1
    );
  } catch (e) {
    return false;
  }
};

export const isValidDateOnlyRange = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const dateRange = value as {
    start?: string | null;
    end?: string | null;
  };
  const hasStartProperty = 'start' in dateRange;
  const hasEndProperty = 'end' in dateRange;

  if (!hasStartProperty || !hasEndProperty) {
    return false;
  }

  const { start, end } = dateRange;
  const isStartDate = typeof start === 'string' && isISO8601DateOnly(start);
  const isEndDate = typeof end === 'string' && isISO8601DateOnly(end);

  if (isStartDate && isEndDate) {
    return start <= end; // ISO date strings can be compared directly
  }

  const isStartValid = start === null || start === undefined || isStartDate;
  const isEndValid = end === null || end === undefined || isEndDate;
  return isStartValid && isEndValid;
};

export const isValidDateTimeRange = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const dateTimeRange = value as {
    start?: string | null;
    end?: string | null;
  };
  const hasStartProperty = 'start' in dateTimeRange;
  const hasEndProperty = 'end' in dateTimeRange;

  if (!hasStartProperty || !hasEndProperty) {
    return false;
  }

  const { start, end } = dateTimeRange;
  const isStartDateTime = typeof start === 'string' && isISO8601DateTime(start);
  const isEndDateTime = typeof end === 'string' && isISO8601DateTime(end);

  if (isStartDateTime && isEndDateTime) {
    return start <= end; // ISO date-time strings can be compared directly
  }

  const isStartValid = start === null || start === undefined || isStartDateTime;
  const isEndValid = end === null || end === undefined || isEndDateTime;
  return isStartValid && isEndValid;
};
